import { api } from './api';
import type { SupportedLanguage } from '@/shared/i18n/languages';

export interface UserProfile {
  id: string;
  email: string;
  name: string | null;
  avatarUrl: string | null;
}

export interface UpdateProfileInput {
  name?: string;
  /** null removes the photo (an empty string fails the API's URL check). */
  avatarUrl?: string | null;
}

/**
 * Profile read/write against /auth/me. The shape mirrors what the team list
 * and dashboard greeting already consume from Supabase user_metadata.
 */
export type NotificationKey =
  | 'post_published'
  | 'post_failed'
  | 'weekly_report'
  | 'best_time_reminder'
  | 'platform_error_alert'
  | 'inbox_sla_breach'
  | 'team_invite';

export type NotificationPreferences = Record<NotificationKey, boolean>;

export const profileService = {
  async getMe(): Promise<UserProfile> {
    const res = await api.get<UserProfile>('/auth/me');
    return res.data;
  },

  /** PUT /auth/me/language: the interface language, which emails are written in. */
  async updateLanguage(language: SupportedLanguage): Promise<{ language: SupportedLanguage }> {
    const res = await api.put<{ language: SupportedLanguage }>('/auth/me/language', { language });
    return res.data;
  },

  async updateMe(input: UpdateProfileInput): Promise<UserProfile> {
    const res = await api.patch<UserProfile>('/auth/me', input);
    return res.data;
  },

  async getNotificationPreferences(): Promise<NotificationPreferences> {
    const res = await api.get<NotificationPreferences>('/auth/me/notifications');
    return res.data;
  },

  async updateNotificationPreferences(
    patch: Partial<NotificationPreferences>,
  ): Promise<NotificationPreferences> {
    const res = await api.patch<NotificationPreferences>('/auth/me/notifications', patch);
    return res.data;
  },

  /**
   * Two-step avatar upload:
   *   1. POST /auth/me/avatar/presign → { uploadUrl, publicUrl }
   *   2. PUT the file to uploadUrl
   *   3. PATCH /auth/me with avatarUrl: publicUrl
   *
   * The PATCH happens in the calling component via useUpdateProfile so
   * React Query caches stay consistent.
   */
  async uploadAvatar(file: File): Promise<{ publicUrl: string }> {
    const presignRes = await api.post<{ uploadUrl: string; publicUrl: string; s3Key: string }>(
      '/auth/me/avatar/presign',
      { contentType: file.type, sizeBytes: file.size },
    );
    const { uploadUrl, publicUrl } = presignRes.data;

    // PUT directly to S3 — don't proxy through our API to save bandwidth.
    const uploadRes = await fetch(uploadUrl, {
      method: 'PUT',
      headers: { 'Content-Type': file.type },
      body: file,
    });
    if (!uploadRes.ok) {
      throw new Error(`S3 upload failed (HTTP ${uploadRes.status}).`);
    }
    return { publicUrl };
  },
};
